import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createChatSocket, type ChatSocketLike } from '@/api/chat-realtime';

// =============================================================================
// use-chat-realtime.ts — Hook temps réel du chat (COM-01, DEC-31 §1/§3).
// =============================================================================
// Le socket est LIVRAISON-SEULE : il ne fait QUE déclencher des invalidations TanStack Query. Le CONTENU
// vient TOUJOURS du refetch REST re-cloisonné par le RLS (DEC-31). AUCUNE donnée métier n'est dérivée du
// payload socket — le hook n'accède JAMAIS aux champs du payload : sa seule information est « un événement
// est arrivé sur une room jointe », ce qui suffit à décider quoi refetcher.
//
// Câblage :
//   - à l'ouverture d'un fil → `socket.emit('thread:join', { threadId })` (JOIN gardé RLS côté serveur :
//     un fil non autorisé ⇒ pas de join, aucun événement reçu) ;
//   - `message:new` / `message:read` → invalide les messages du fil OUVERT (threadId des ARGUMENTS du
//     hook, jamais du payload) + la liste des fils `['chat-threads']` ;
//   - `chat:activity` → invalide UNIQUEMENT la liste `['chat-threads']` (badge silencieux : AUCUN son,
//     AUCUNE notification OS, AUCUN e-mail).

/** Noms d'événements REÇUS (payload mince, jamais lu par le hook). */
const EVENT_MESSAGE_NEW = 'message:new';
const EVENT_MESSAGE_READ = 'message:read';
const EVENT_CHAT_ACTIVITY = 'chat:activity';
/** Événement ÉMIS à l'ouverture d'un fil (JOIN gardé RLS côté serveur). */
const EVENT_THREAD_JOIN = 'thread:join';

/** Clé de messages d'un fil (dupliquée volontairement pour éviter un cycle d'import avec use-chat). */
function threadMessagesKeyPrefix(): readonly unknown[] {
  return ['chat-thread-messages'];
}

export interface UseChatRealtimeOptions {
  /** Fabrique de socket (injectable pour les tests — double sans réseau). Défaut : createChatSocket. */
  readonly socketFactory?: () => ChatSocketLike;
}

/**
 * Établit le socle temps réel du chat. Un SEUL socket par montage du hook ; il rejoint le fil ouvert
 * (`thread:join`) et invalide les queries à chaque signal. Aucun contenu n'est lu du payload socket.
 *
 * @param threadId fil actuellement ouvert (état d'URL), ou `null` si aucun fil n'est sélectionné.
 * @param options fabrique de socket injectable (tests).
 */
export function useChatRealtime(
  threadId: string | null,
  options: UseChatRealtimeOptions = {},
): void {
  const queryClient = useQueryClient();
  const socketFactory = options.socketFactory ?? createChatSocket;
  // Ref sur la fabrique : capturée une fois au montage (le socket vit toute la durée du montage).
  const socketFactoryRef = React.useRef(socketFactory);
  const socketRef = React.useRef<ChatSocketLike | null>(null);

  // Cycle de vie du socket + abonnement aux signaux. Un seul effet au montage/démontage : le socket
  // survit aux changements de fil (on ré-émet seulement `thread:join`, voir l'effet suivant).
  React.useEffect(() => {
    const socket = socketFactoryRef.current();
    socketRef.current = socket;

    // NB : les handlers n'ACCÈDENT PAS au payload — seul le TYPE d'événement pilote l'invalidation.
    const onThreadEvent = (): void => {
      // Signal de niveau fil : refetch des messages du fil ouvert + de la liste (récence/aperçu/non-lus).
      void queryClient.invalidateQueries({ queryKey: threadMessagesKeyPrefix() });
      void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
    };
    const onActivity = (): void => {
      // Signal de niveau tenant (badge silencieux) : refetch de la liste UNIQUEMENT.
      void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
    };

    socket.on(EVENT_MESSAGE_NEW, onThreadEvent);
    socket.on(EVENT_MESSAGE_READ, onThreadEvent);
    socket.on(EVENT_CHAT_ACTIVITY, onActivity);

    return () => {
      socket.off(EVENT_MESSAGE_NEW, onThreadEvent);
      socket.off(EVENT_MESSAGE_READ, onThreadEvent);
      socket.off(EVENT_CHAT_ACTIVITY, onActivity);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient]);

  // À l'ouverture (ou au changement) d'un fil : rejoindre sa room. Le JOIN est GARDÉ RLS côté serveur
  // (un fil non autorisé ⇒ pas de join). Le nom de room est construit SERVEUR-side ; on n'envoie que l'id.
  React.useEffect(() => {
    const socket = socketRef.current;
    if (socket === null || threadId === null || threadId.length === 0) {
      return;
    }
    socket.emit(EVENT_THREAD_JOIN, { threadId });
  }, [threadId]);
}
